import AmpEvents from '@amplify/amp-events';

import Env from 'lib/Env';
import { SEARCHLIGHT_EVENT_TYPE, DESTINATION_DESCRIPTIONS } from 'lib/SearchlightDispatcher';
import AuthService from 'root/lib/authService';
import { getNewDate, getTimestamp } from 'lib/DatetimeManager';
import { SEARCHLIGHT_ACTION_TYPE } from 'a/actions/searchlightActionCreators';

import packageJson from '../../../package.json';
import GAME_IDS from '../hub/data/GameIds';
import { SEASON, FORMATTED_GRADES } from '../hub/data/constants';
import { getLanguage } from '../selectors/languageSelectors';

const APP_VERSION = packageJson.version;
const APP_NAME = 'amplify-reading';

let ampEvents = null;
const initializeAmpEventsWithStudentId = (userId) => {
  if (!userId) {
    ampEvents = null;
    return;
  }
  ampEvents = new AmpEvents({
    [AmpEvents.OPTIONS.HOMEPAGE]: 'https://reading.amplify.com',
    [AmpEvents.OPTIONS.APP_NAME]: APP_NAME,
    [AmpEvents.OPTIONS.APP_VERSION]: APP_VERSION,
    [AmpEvents.OPTIONS.LANGUAGE]: 'en-US',
    [AmpEvents.OPTIONS.ENVIRONMENT]: Env.isProd ? AmpEvents.ENVIRONMENTS.PRODUCTION : AmpEvents.ENVIRONMENTS.DEVCI,
    [AmpEvents.OPTIONS.USER_ID]: userId,
  });
};

const lastEventDate = {
  value: null,
  getElapsedTime() {
    const date = getNewDate();
    if (!this.value) {
      return 0;
    }
    const elapsed = date - this.value;
    this.value = date;
    return `${elapsed}ms`;
  },
};

const firstEventDate = {
  value: null,
  getElapsedTime() {
    const date = getNewDate();
    if (!this.value) {
      this.value = date;
    }
    const elapsed = date.getTime() - this.value.getTime();
    return `${elapsed}ms`;
  },
};

let showXapiLogs = false;

if (Env.isDevEnv) {
  showXapiLogs = showXapiLogs || sessionStorage.getItem('showXapiLogs') === 'true';
}

const XApiMiddleware = store => next => (action) => {
  const { type, payload = { application: {} } } = action;
  // if not a searchlight dispatch event, move along immediately
  if (![
    SEARCHLIGHT_ACTION_TYPE.DISPATCH_EVENT,
    SEARCHLIGHT_ACTION_TYPE.SEARCHLIGHT_DISPATCH_EVENT,
  ].includes(type)) {
    return next(action);
  }

  // it is a searchlight event, get the searchlight event type
  const { application: { eventType } } = payload;
  try {
    // gather and organize data available for sending events
    const eventData = getEventData(action, store);
    // check if it's the same student. if it is not, re-initialize amp events
    if (ampEvents === null || eventData.studentId !== ampEvents.config.userId) {
      initializeAmpEventsWithStudentId(eventData.studentId);
    }
    if (!ampEvents) {
      return next(action);
    }
    const event = ampEvents.eventService.createEvent();

    switch (eventType) {
      case SEARCHLIGHT_EVENT_TYPE.ROUND_START:
        parseRoundStartEvent(event, eventData);
        break;
      case SEARCHLIGHT_EVENT_TYPE.ROUND_COMPLETE:
        parseRoundCompleteEvent(event, eventData);
        store.dispatch({ type: SEARCHLIGHT_ACTION_TYPE.CLEAR_SEARCHLIGHT_DATA });
        break;
      case SEARCHLIGHT_EVENT_TYPE.NAVIGATION:
        parseNavigationEvent(event, eventData);
        break;
      case SEARCHLIGHT_EVENT_TYPE.START_ASSESSMENT:
        parseAssessmentStartEvent(event, eventData);
        break;
      case SEARCHLIGHT_EVENT_TYPE.COMPLETE_ASSESSMENT:
        parseAssessmentCompleteEvent(event, eventData);
        break;
      default:
        break;
    }
    addCommonFields(event, eventData);
    // if event statement verb has been modified from the default, emit event
    if (event.statement.verb.id !== 'not-set') {
      if (Env.isDevEnv && showXapiLogs) {
        console.log('amp-event emitting:', event.statement);
      }
      event.emit();
    }
  } catch (e) {
    console.error(`There was a problem creating the xAPI event: ${eventType}\n.`, e);
  }

  // either way, we're done here, move along
  return next(action);
};

const parseRoundStartEvent = (event, {
  gameId,
  gameName,
  level,
  isQuestWorkflow,
  isQuestInDemoMode,
  isIntermediateSave,
  season,
  questId,
  roundSessionId,
  bookId,
  chapterId,
}) => {
  ampEvents.eventService.addVerb(event, 'initialized');

  if (gameId === GAME_IDS.E_READER) {
    ampEvents.eventService.addObject(event, `${gameId}.${bookId}.${chapterId}`);
    event.addObjectDefinition('name', `${gameId}-${bookId}-${chapterId}`);
    event.addObjectDefinition('description', `${gameName}-book-${bookId}-chapter-${chapterId}`);
  } else {
    ampEvents.eventService.addObject(event, `${gameId}.${level}`);
    event.addObjectDefinition('name', `${gameName} - Level ${level}`);
    event.addObjectDefinition('description', `${gameId}-level-${level}`);
  }

  ampEvents.eventService.addContextExtensions(event, {
    'is-quest-workflow': !!isQuestWorkflow,
    'is-quest-in-demo-mode': !!isQuestInDemoMode,
    'is-intermediate-save': !!isIntermediateSave,
    'round-session-id': roundSessionId,
    season,
    quest: questId,
  });

  return event;
};

const parseRoundCompleteEvent = (event, {
  gameId,
  gameName,
  level,
  questionCount,
  questionsCorrect,
  isEarlyExit,
  isQuestWorkflow,
  isQuestInDemoMode,
  isIntermediateSave,
  season,
  questId,
  roundSessionId,
  bookId,
  chapterId,
}) => {
  ampEvents.eventService.addVerb(event, 'completed');

  if (gameId === GAME_IDS.E_READER) {
    ampEvents.eventService.addObject(event, `${gameId}.${bookId}.${chapterId}`);
    event.addObjectDefinition('name', `${gameId}-${bookId}-${chapterId}`);
    event.addObjectDefinition('description', `${gameName}-book-${bookId}-chapter-${chapterId}`);
  } else {
    ampEvents.eventService.addObject(event, `${gameId}.${level}`);
    event.addObjectDefinition('name', `${gameName} - Level ${level}`);
    event.addObjectDefinition('description', `${gameId}-level-${level}`);
  }

  ampEvents.eventService.addContextExtensions(event, {
    'is-quest-workflow': !!isQuestWorkflow,
    'is-quest-in-demo-mode': !!isQuestInDemoMode,
    'is-intermediate-save': !!isIntermediateSave,
    'round-session-id': roundSessionId,
    season,
    quest: questId,
  });

  ampEvents.eventService.addResultExtensions(event, {
    'question-count': questionCount,
    'question-correct': questionsCorrect,
    'is-early-exit': !!isEarlyExit,
  });

  return event;
};

const parseNavigationEvent = (event, {
  destination,
  destinationGroup,
  destinationDescription,
  trigger,
  triggerType,
  grade,
  origin,
}) => {
  ampEvents.eventService.addVerb(event, 'viewed');

  ampEvents.eventService.addObject(event, `reading.G${grade}.${destinationGroup}`);

  event.addObjectDefinition('name', `${destination}`);
  event.addObjectDefinition('description', `${destinationDescription}`);

  ampEvents.eventService.addContextExtensions(event, {
    'from-route-name': `reading.G${grade}.${origin}`,
    'event-trigger': trigger,
    'event-trigger-type': triggerType,
  });
  return event;
};

const parseAssessmentStartEvent = (event, {
  gameId,
  isPlacementAPT,
  grade,
  measures,
  timeOfYear,
  schoolYearEnd,
  benchmarkId,
}) => {
  ampEvents.eventService.addVerb(event, 'started');
  ampEvents.eventService.addObject(event, gameId);
  ampEvents.eventService.addContextExtensions(event, {
    'user-enrollment-source': isPlacementAPT ? 'mclass' : 'onboarding',
    'user-enrollment-grade': FORMATTED_GRADES[grade] || grade,
    measures,
    'time-of-year': timeOfYear,
    'school-year': schoolYearEnd,
    'benchmark-assessment-guid': benchmarkId,
  });

  return event;
};

const parseAssessmentCompleteEvent = (event, {
  gameId,
  isPlacementAPT,
  grade,
  measures,
  timeOfYear,
  schoolYearEnd,
  rwrId,
  mtgIdStory1,
  mtgIdStory2,
  benchmarkId,
  result_id,
}) => {
  ampEvents.eventService.addVerb(event, 'completed');
  ampEvents.eventService.addObject(event, gameId);
  ampEvents.eventService.addContextExtensions(event, {
    'user-enrollment-source': isPlacementAPT ? 'mclass' : 'onboarding',
    'user-enrollment-grade': FORMATTED_GRADES[grade] || grade,
    'time-of-year': timeOfYear,
    'school-year': schoolYearEnd,
    'measures-taken': measures,
    'real-word-reading-attempt-guid': rwrId,
    'text-reading-online-measure-attempt-guid': result_id || null,
    'mind-the-gap-story1-attempt-guid': mtgIdStory1,
    'mind-the-gap-story2-attempt-guid': mtgIdStory2,
    'benchmark-assessment-guid': benchmarkId,
  });

  return event;
};

const addCommonFields = (event, {
  appVersion,
  sessionId,
  application,
  sessionDuration,
  timeSinceLastEvent,
  keycloakSessionId,
  timestamp,
  userType,
  isTestUser,
  grade,
  organization,
  language,
}) => {
  event.setTimestamp(timestamp);
  ampEvents.eventService.addContextExtensions(event, {
    'keycloak-session-id': keycloakSessionId,
    'app-version': appVersion,
    'session-id': sessionId,
    'session-duration': sessionDuration,
    'time-since-last-event': timeSinceLastEvent,
    'library-version': 'n/a',
    'user-type': userType,
    'is-test-user': isTestUser,
    application,
    organization,
    grade,
    language,
  });
};

const formatString = str => str.replace(/([A-Z]+)/g, '-$1').toLowerCase();

const parseGameNameFromId = (id) => {
  const name = id.replace(/_/g, ' ');
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const getEventData = (action, store) => {
  const state = store.getState();
  const {
    payload: {
      application: {
        gameId = 'not set',
        details = {},
      } = {},
    },
  } = action;

  // setting default values for some details
  const {
    // used for navigation
    destination = 'not set',
    destinationGroup = 'not set',
    origin = 'not set',
    // used in the eReader
    chapterId = 0,
    isEarlyExit,
    isIntermediateSave,
    isQuestInDemoMode,
    isQuestWorkflow,
    level,
    measures,
    questionCount,
    questionsCorrect,
    schoolYearEnd,
    timeOfYear,
    trigger,
    triggerType,
  } = details;

  const {
    persistStateReducer: {
      sessionId,
      grade,
      seasonPart,
      firstName,
      lastName,
      username,
      studentId,
      isTestUser,
      organization,
      isStaffLogin,
      isPlacementAPT,
    },
    appSessionReducer: {
      searchlight: {
        roundSessionId,
      },
    },
    placementTool: {
      roundSessionIds: {
        rwrId,
        mtgIdStory1,
        mtgIdStory2,
        benchmarkId,
      },
      troData: {
        result_id,
      },
    },
  } = state;

  const { questWorkflowReducer: { season, currentActiveQuestId } } = state;

  const { workshopReducer: { currentQuestId: workshopQuestId } } = state;
  let currentQuestId = season === SEASON.GRADE_4_5 ? workshopQuestId : currentActiveQuestId;
  if (parseInt(currentQuestId, 10) >= 100) {
    currentQuestId = parseInt(currentQuestId.toString().substring(1), 10);
  }

  const destinationDescription = DESTINATION_DESCRIPTIONS[destinationGroup] || 'n/a';

  return {
    application: 'K-5',
    appVersion: APP_VERSION,
    benchmarkId,
    chapterId,
    destination: formatString(destination),
    destinationDescription,
    destinationGroup: formatString(destinationGroup),
    firstName,
    gameId,
    gameName: parseGameNameFromId(gameId),
    grade,
    isPlacementAPT,
    isEarlyExit,
    isIntermediateSave,
    isQuestInDemoMode,
    isQuestWorkflow,
    isTestUser,
    keycloakSessionId: AuthService.getSessionId(),
    language: getLanguage(state),
    lastName,
    level,
    measures,
    mtgIdStory1,
    mtgIdStory2,
    organization,
    origin: formatString(origin),
    quest: currentQuestId !== 0 ? `Quest ${currentQuestId}` : 'Challenge Quest',
    questId: currentQuestId.toString(),
    questionCount,
    questionsCorrect,
    result_id,
    roundSessionId,
    rwrId,
    schoolYearEnd,
    season,
    seasonPart,
    sessionDuration: firstEventDate.getElapsedTime(),
    sessionId,
    source: APP_NAME,
    studentId,
    timeOfYear,
    timeSinceLastEvent: lastEventDate.getElapsedTime(),
    timestamp: getTimestamp(),
    trigger,
    triggerType,
    username,
    userType: isStaffLogin ? 'teacher' : 'student',
    ...details,
  };
};

export default XApiMiddleware;
